import { useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useFirestore } from '../../../helpers/firebaseContext';
import { TemplateDoc, TemplateDocSchema } from '../../../types/geneva-schema';
import { SchemaType } from '../../../types/schema';
import { DEFAULT_SCHEMA_TYPE, SCHEMA_CONFIG } from '../../../utils/schema-config';

interface UseGenevaSchemaServiceProps {
  orgId: string;
  templateId: string;
  userId: string;
  userEmail: string;
  schemaType?: SchemaType;
}

export const useGenevaSchemaService = ({
  orgId,
  templateId,
  userId,
  userEmail,
  schemaType = DEFAULT_SCHEMA_TYPE,
}: UseGenevaSchemaServiceProps) => {
  const firestore = useFirestore();
  const schemaDefaults = SCHEMA_CONFIG[schemaType];

  const getTemplateDocRef = useCallback(() => {
    return doc(firestore, 'orgs', orgId, schemaDefaults.templateCollection, templateId);
  }, [firestore, orgId, templateId, schemaDefaults.templateCollection]);

  const loadTemplate = useCallback(async (): Promise<TemplateDoc> => {
    const docRef = getTemplateDocRef();
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Template not found');
    }

    const data = docSnap.data();
    return TemplateDocSchema.parse(data);
  }, [getTemplateDocRef]);

  const saveTemplate = useCallback(async (template: TemplateDoc): Promise<void> => {
    const docRef = getTemplateDocRef();

    // Update metadata
    const updatedTemplate = {
      ...template,
      schemaType: template.schemaType ?? schemaType,
      updatedAt: Date.now(),
      updatedBy: userEmail,
    };

    // Validate before saving
    const validatedTemplate = TemplateDocSchema.parse(updatedTemplate);

    const pruneUndefinedAndEmpty = (val: any): any => {
      if (Array.isArray(val)) {
        const cleaned = val
          .map((item) => pruneUndefinedAndEmpty(item))
          .filter((item) => item !== undefined);
        return cleaned;
      }
      if (val && typeof val === 'object') {
        const entries = Object.entries(val)
          .map(([k, v]) => [k, pruneUndefinedAndEmpty(v)] as const)
          .filter(([, v]) => v !== undefined);
        if (entries.length === 0) {
          return undefined;
        }
        return Object.fromEntries(entries);
      }
      return val === undefined ? undefined : val;
    };

    const cleanedTemplate = pruneUndefinedAndEmpty(validatedTemplate) as TemplateDoc;

    console.log('[saveTemplate] Cleaned template fieldOverrides:', cleanedTemplate.fieldOverrides);


    console.log('[saveTemplate] Saving template:', cleanedTemplate, "to", docRef.path);
    await setDoc(docRef, cleanedTemplate);
  }, [getTemplateDocRef, userEmail, schemaType]);

  const createTemplate = useCallback(async (name: string, description?: string): Promise<TemplateDoc> => {
    const newTemplate: TemplateDoc = {
      schemaVersion: 'v1',
      enabledRecords: {},
      fieldOverrides: {},
      updatedAt: Date.now(),
      updatedBy: userEmail,
      name,
      description,
      schemaType,
    };

    await saveTemplate(newTemplate);
    return newTemplate;
  }, [saveTemplate, userEmail, schemaType]);

  const duplicateTemplate = useCallback(async (
    sourceTemplate: TemplateDoc,
    newName: string,
    newTemplateId: string
  ): Promise<TemplateDoc> => {
    const newTemplate: TemplateDoc = {
      ...sourceTemplate,
      schemaType,
      name: newName,
      updatedAt: Date.now(),
      updatedBy: userEmail,
    };

    const newDocRef = doc(firestore, 'orgs', orgId, schemaDefaults.templateCollection, newTemplateId);
    await setDoc(newDocRef, newTemplate);

    return newTemplate;
  }, [orgId, userEmail, schemaDefaults.templateCollection, schemaType]);

  const deleteTemplate = useCallback(async (): Promise<void> => {
    const docRef = getTemplateDocRef();
    // In a real implementation, you might want to use deleteDoc here
    // For now, we'll just mark it as deleted
    await setDoc(docRef, { deleted: true, updatedAt: Date.now() }, { merge: true });
  }, [getTemplateDocRef]);

  const listTemplates = useCallback(async (): Promise<Array<{ id: string; name: string; updatedAt: number }>> => {
    // This would require a collection query
    // For now, return empty array as we're focusing on single template operations
    return [];
  }, []);

  return {
    loadTemplate,
    saveTemplate,
    createTemplate,
    duplicateTemplate,
    deleteTemplate,
    listTemplates,
  };
}; 
